import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRoute,
    faMoneyBillWave,
    faChevronDown,
    faChevronUp,
    faChevronLeft,
    faChevronRight,
    faMapMarkerAlt,
    faArrowRight,
    faCheckCircle,
    faHourglassHalf,
    faClock,
    faTimesCircle,
    faStar,
    faInfoCircle,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const SupervisorFraisDeplacement = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedProject, setExpandedProject] = useState(null);
    const [projectDetail, setProjectDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedAdvance, setSelectedAdvance] = useState(null);
    const advanceDismiss = useModalDismiss(() => setSelectedAdvance(null), { enabled: !!selectedAdvance });
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 5;

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await api.get('/f2f/supervisor/frais-deplacement');
            if (response.data.success) {
                setProjects(response.data.data);
            } else {
                setError(response.data.message || 'Erreur lors du chargement');
            }
        } catch (err) {
            console.error('Error fetching frais deplacement:', err);
            setError('Erreur lors du chargement des frais de déplacement');
        } finally {
            setLoading(false);
        }
    };

    const fetchDetail = async (projectId) => {
        try {
            setDetailLoading(true);
            const response = await api.get(`/f2f/supervisor/frais-deplacement/${projectId}`);
            if (response.data.success) {
                setProjectDetail(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching detail:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    // Filtering & pagination
    const filteredProjects = projects.filter(p => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (p.survey?.title || '').toLowerCase().includes(q) ||
               (p.status || '').toLowerCase().includes(q);
    });
    const totalPages = Math.max(1, Math.ceil(filteredProjects.length / perPage));
    const paginatedProjects = filteredProjects.slice((currentPage - 1) * perPage, currentPage * perPage);

    const handleSearchChange = (val) => {
        setSearchQuery(val);
        setCurrentPage(1);
        setExpandedProject(null);
        setProjectDetail(null);
    };

    const toggleProject = (projectId) => {
        if (expandedProject === projectId) {
            setExpandedProject(null);
            setProjectDetail(null);
        } else {
            setExpandedProject(projectId);
            fetchDetail(projectId);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getAdvanceStatusBadge = (status) => {
        const map = {
            'pending': { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: faClock },
            'approved': { label: 'Approuvée', color: 'bg-blue-100 text-blue-700', icon: faCheckCircle },
            'paid': { label: 'Payée', color: 'bg-green-100 text-green-700', icon: faCheckCircle },
            'reported': { label: 'Reportée', color: 'bg-orange-100 text-orange-700', icon: faHourglassHalf },
        };
        const info = map[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: faInfoCircle };
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
                <FontAwesomeIcon icon={info.icon} className="mr-1" style={{ fontSize: '10px' }} />
                {info.label}
            </span>
        );
    };

    const getStatusLabel = (status) => {
        const map = {
            'validated': 'Validé',
            'submitted': 'Soumis',
            'locked': 'Verrouillé',
        };
        return map[status] || status;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement des frais de déplacement...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faTimesCircle} className="text-5xl text-red-600 mb-4" />
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                        Frais de Déplacement
                    </h1>
                    <p className="text-gray-600">Consultez vos circuits, bonus et avances par projet</p>
                </div>

                {/* Summary Cards */}
                {projects.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Budget Total</h3>
                                <FontAwesomeIcon icon={faRoute} className="text-xl text-red-600" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                                {formatCurrency(projects.reduce((sum, p) => sum + parseFloat(p.total || 0), 0))}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Circuits + Bonus</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Avances Totales</h3>
                                <FontAwesomeIcon icon={faMoneyBillWave} className="text-xl text-green-600" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                                {formatCurrency(projects.reduce((sum, p) => sum + parseFloat(p.advances_total || 0), 0))}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Montant total des avances</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Avances Payées</h3>
                                <FontAwesomeIcon icon={faCheckCircle} className="text-xl text-green-600" />
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                                {formatCurrency(projects.reduce((sum, p) => sum + parseFloat(p.advances_paid || 0), 0))}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">Montant effectivement reçu</p>
                        </div>
                    </div>
                )}

                {/* Search Bar */}
                {projects.length > 0 && (
                    <div className="mb-6">
                        <div className="relative">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => handleSearchChange(e.target.value)}
                                placeholder="Rechercher par nom d'enquête..."
                                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                )}

                {/* Projects List */}
                {filteredProjects.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <FontAwesomeIcon icon={searchQuery ? faSearch : faRoute} className="text-5xl text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-600 mb-2">
                            {searchQuery ? 'Aucun résultat' : 'Aucun projet de frais de déplacement'}
                        </h3>
                        <p className="text-gray-500">
                            {searchQuery ? `Aucun projet ne correspond à "${searchQuery}"` : "Vous n'avez pas encore de circuits assignés"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {paginatedProjects.map(project => (
                            <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                {/* Project Header */}
                                <div
                                    className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleProject(project.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {project.survey?.title || `Projet #${project.id}`}
                                                </h3>
                                                {project.survey_wave?.label && (
                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                                        {project.survey_wave.label}
                                                    </span>
                                                )}
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    {getStatusLabel(project.status)}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                                                <span>Budget: <strong className="text-gray-900">{formatCurrency(project.total)}</strong></span>
                                                <span>Avances: <strong className="text-green-700">{formatCurrency(project.advances_total)}</strong></span>
                                                {parseFloat(project.bonuses_total) > 0 && (
                                                    <span>Bonus: <strong className="text-amber-700">{formatCurrency(project.bonuses_total)}</strong></span>
                                                )}
                                            </div>
                                        </div>
                                        <FontAwesomeIcon
                                            icon={expandedProject === project.id ? faChevronUp : faChevronDown}
                                            className="text-gray-400 text-lg ml-4"
                                        />
                                    </div>
                                </div>

                                {/* Expanded Detail */}
                                {expandedProject === project.id && (
                                    <div className="border-t border-gray-200 p-5 bg-gray-50">
                                        {detailLoading ? (
                                            <div className="text-center py-8">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-2"></div>
                                                <p className="text-gray-500 text-sm">Chargement...</p>
                                            </div>
                                        ) : projectDetail ? (
                                            <div className="space-y-6">
                                                {/* Bonuses */}
                                                {projectDetail.bonuses && projectDetail.bonuses.length > 0 && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                                            Bonus
                                                        </h4>
                                                        <div className="space-y-2">
                                                            {projectDetail.bonuses.map(bonus => (
                                                                <div key={bonus.id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                                                                    <span className="text-sm text-gray-700">{bonus.notes || 'Bonus'}</span>
                                                                    <span className="font-semibold text-amber-700">{formatCurrency(bonus.amount)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Advances */}
                                                {projectDetail.advances && projectDetail.advances.length > 0 && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                                            Avances
                                                        </h4>
                                                        {/* Desktop: table with clear column spacing */}
                                                        <div className="hidden sm:block overflow-x-auto">
                                                            <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                                                                <thead>
                                                                    <tr className="text-left text-gray-500">
                                                                        <th className="pb-2 pr-6 font-medium border-b border-gray-200 whitespace-nowrap">Date prévue</th>
                                                                        <th className="pb-2 px-6 font-medium border-b border-gray-200 whitespace-nowrap">Montant</th>
                                                                        <th className="pb-2 pl-6 font-medium border-b border-gray-200 whitespace-nowrap">Statut</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {projectDetail.advances.map(advance => (
                                                                        <tr key={advance.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAdvance(advance)}>
                                                                            <td className="py-2 pr-6 border-b border-gray-100 whitespace-nowrap">{formatDate(advance.planned_date)}</td>
                                                                            <td className="py-2 px-6 font-medium border-b border-gray-100 whitespace-nowrap">{formatCurrency(advance.amount)}</td>
                                                                            <td className="py-2 pl-6 border-b border-gray-100 whitespace-nowrap">{getAdvanceStatusBadge(advance.status)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        {/* Mobile: stacked cards */}
                                                        <div className="sm:hidden space-y-2">
                                                            {projectDetail.advances.map(advance => (
                                                                <div
                                                                    key={advance.id}
                                                                    className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50"
                                                                    onClick={() => setSelectedAdvance(advance)}
                                                                >
                                                                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                                                                        <span className="text-xs text-gray-500 uppercase tracking-wide">Date prévue</span>
                                                                        <span className="text-sm text-gray-900">{formatDate(advance.planned_date)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                                        <span className="text-xs text-gray-500 uppercase tracking-wide">Montant</span>
                                                                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(advance.amount)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center pt-2">
                                                                        <span className="text-xs text-gray-500 uppercase tracking-wide">Statut</span>
                                                                        <span>{getAdvanceStatusBadge(advance.status)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Total Summary */}
                                                <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
                                                    <span className="font-medium text-gray-700">Total Budget</span>
                                                    <span className="text-xl font-bold text-gray-900">{formatCurrency(projectDetail.total)}</span>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {filteredProjects.length > perPage && (
                    <div className="flex items-center justify-between mt-6">
                        <p className="text-sm text-gray-600">
                            {filteredProjects.length} projet{filteredProjects.length > 1 ? 's' : ''} — page {currentPage} / {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); setExpandedProject(null); setProjectDetail(null); }}
                                disabled={currentPage === 1}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${currentPage === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer'}`}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="mr-1" />
                                Précédent
                            </button>
                            <button
                                onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); setExpandedProject(null); setProjectDetail(null); }}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer'}`}
                            >
                                Suivant
                                <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ADVANCE DETAIL MODAL */}
            {selectedAdvance && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} {...advanceDismiss}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '90%' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Détails de l'avance</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                <span style={{ fontSize: 13, color: '#64748b' }}>Montant</span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{parseFloat(selectedAdvance.amount).toLocaleString('fr-FR')} MAD</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                <span style={{ fontSize: 13, color: '#64748b' }}>Statut</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color:
                                    selectedAdvance.status === 'approved' ? '#10b981' :
                                    selectedAdvance.status === 'paid' ? '#16a34a' :
                                    selectedAdvance.status === 'reported' ? '#f97316' : '#64748b'
                                }}>
                                    {selectedAdvance.status === 'approved' ? 'Approuvée' :
                                     selectedAdvance.status === 'paid' ? 'Payée' :
                                     selectedAdvance.status === 'reported' ? 'Reportée' : selectedAdvance.status}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                <span style={{ fontSize: 13, color: '#64748b' }}>Date prévue</span>
                                <span style={{ fontSize: 13, color: '#1e293b' }}>{formatDate(selectedAdvance.planned_date)}</span>
                            </div>
                            {selectedAdvance.notes && (
                                <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</span>
                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.notes}</span>
                                </div>
                            )}
                            {selectedAdvance.created_by && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>Créée par</span>
                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.created_by?.name || '-'}</span>
                                </div>
                            )}
                            {selectedAdvance.approved_by && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>Approuvée par</span>
                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{selectedAdvance.approved_by?.name || '-'}</span>
                                </div>
                            )}
                            {selectedAdvance.approved_at && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>Date d'approbation</span>
                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{new Date(selectedAdvance.approved_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                            )}
                            {selectedAdvance.paid_at && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f0fdf4', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#64748b' }}>Date de paiement</span>
                                    <span style={{ fontSize: 13, color: '#1e293b' }}>{new Date(selectedAdvance.paid_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <button onClick={() => setSelectedAdvance(null)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupervisorFraisDeplacement;
